// MapsSortMenu - ビルダー一覧の並び替え（3タブ共通）。
// トリガーボタン → 右側に吹き出しメニュー（軸 + むき）。下書きを編集し確定で適用。

import { useState } from "react";
import I18n from "common/i18n";
import { PieceSort, PieceSortOrder, isDateAxis } from "features/builder/pieceList";
import { MenuOptionRow } from "./MenuOptionRow";
import { SidebarDropdown } from "./SidebarDropdown";
import { MenuActionFooter } from "./MenuActionFooter";

const AXIS_LABEL_KEY: Record<PieceSort, string> = {
  [PieceSort.UPDATED]: "MSG_BUILDER_SORT_AXIS_UPDATED",
  [PieceSort.DELETED]: "MSG_BUILDER_SORT_AXIS_DELETED",
  [PieceSort.LIKED]: "MSG_BUILDER_SORT_AXIS_LIKED",
  [PieceSort.PLAYED]: "MSG_BUILDER_SORT_AXIS_PLAYED",
  [PieceSort.VIEWED]: "MSG_BUILDER_SORT_AXIS_VIEWED",
  [PieceSort.REACTION]: "MSG_BUILDER_SORT_AXIS_REACTION",
};

const AXIS_ICON: Record<PieceSort, string> = {
  [PieceSort.UPDATED]: "calendar_month",
  [PieceSort.DELETED]: "delete",
  [PieceSort.LIKED]: "favorite",
  [PieceSort.PLAYED]: "play_arrow",
  [PieceSort.VIEWED]: "visibility",
  [PieceSort.REACTION]: "mood",
};

// 吹き出しの中身（開くたび再マウントされ、下書きは適用済みから初期化）
const SortPanel = ({
  axis,
  order,
  options,
  accentBgClass,
  onApply,
  onClose,
}: {
  axis: PieceSort;
  order: PieceSortOrder;
  options: PieceSort[];
  accentBgClass: string;
  onApply: (axis: PieceSort, order: PieceSortOrder) => void;
  onClose: () => void;
}) => {
  const [draftAxis, setDraftAxis] = useState(axis);
  const [draftOrder, setDraftOrder] = useState(order);
  const canApply = draftAxis !== axis || draftOrder !== order;
  const descLabelKey = isDateAxis(draftAxis) ? "MSG_BUILDER_SORT_DESC" : "MSG_BUILDER_SORT_MANY";
  const ascLabelKey = isDateAxis(draftAxis) ? "MSG_BUILDER_SORT_ASC" : "MSG_BUILDER_SORT_FEW";

  return (
    <div className="flex-col-el items-start w-full gap-3">
      <div className="flex-col-el items-start w-full gap-2">
        <p className="type-text type-sm text-textcolor-black/60 px-1">
          {I18n.t("MSG_BUILDER_SECTION_SORT")}
        </p>
        <div className="grid grid-cols-2 gap-2 w-full">
          {options.map((opt) => (
            <MenuOptionRow
              key={opt}
              icon={AXIS_ICON[opt]}
              labelKey={AXIS_LABEL_KEY[opt]}
              active={opt === draftAxis}
              activeBgClass={accentBgClass}
              onClick={() => setDraftAxis(opt)}
            />
          ))}
        </div>
      </div>

      <div className="w-full border-t-2 border-solid border-beige-60" />

      <div className="flex-col-el items-start w-full gap-2">
        <p className="type-text type-sm text-textcolor-black/60 px-1">
          {I18n.t("MSG_BUILDER_SORT_ORDER")}
        </p>
        <div className="grid grid-cols-2 gap-2 w-full">
          <MenuOptionRow
            icon="arrow_downward"
            labelKey={descLabelKey}
            active={draftOrder === PieceSortOrder.DESC}
            activeBgClass={accentBgClass}
            onClick={() => setDraftOrder(PieceSortOrder.DESC)}
          />
          <MenuOptionRow
            icon="arrow_upward"
            labelKey={ascLabelKey}
            active={draftOrder === PieceSortOrder.ASC}
            activeBgClass={accentBgClass}
            onClick={() => setDraftOrder(PieceSortOrder.ASC)}
          />
        </div>
      </div>

      <MenuActionFooter
        canApply={canApply}
        accentBgClass={accentBgClass}
        onApply={() => {
          onApply(draftAxis, draftOrder);
          onClose();
        }}
        secondaryLabel="MSG_STOP_BTN"
        onSecondary={onClose}
      />
    </div>
  );
};

export const MapsSortMenu = ({
  axis,
  order,
  options,
  accentTextClass,
  accentBgClass,
  accentBorderClass,
  onChange,
}: {
  axis: PieceSort;
  order: PieceSortOrder;
  options: PieceSort[];
  accentTextClass: string;
  accentBgClass: string;
  accentBorderClass: string;
  onChange: (axis: PieceSort, order: PieceSortOrder) => void;
}) => (
  <SidebarDropdown
    icon={order === PieceSortOrder.DESC ? "arrow_downward" : "arrow_upward"}
    label={AXIS_LABEL_KEY[axis]}
    modalTitle="MSG_BUILDER_SECTION_SORT"
    modalIcon="swap_vert"
    accentTextClass={accentTextClass}
    accentBgClass={accentBgClass}
    accentBorderClass={accentBorderClass}
    active
    wide
    nudgeUp
  >
    {(close) => (
      <SortPanel
        axis={axis}
        order={order}
        options={options}
        accentBgClass={accentBgClass}
        onApply={onChange}
        onClose={close}
      />
    )}
  </SidebarDropdown>
);
